// ============================================================
// useRoster - owns the unified combatant roster and the two LOCAL
// selection pointers.
//
// The roster is shared session state; who you are ACTING AS and who you
// are TARGETING are private to this browser, exactly as
// `localCurrentProfile` already was. Two players can therefore prepare
// attacks on different targets at once without fighting over a pointer.
//
// During phase B this hook also serves the legacy `tabs` / `setTabs`
// interface, so the whole component tree keeps working untouched while
// the data model changes underneath it. Those three members are marked
// LEGACY SHIM and disappear in phase C.
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { Tab } from '../types';
import type { Combatant, CombatState } from '../engine/roster/types';
import { bumpRev, stampSheetRevs } from '../engine/roster/rev';
import type { LegacySession, MigrationResult } from '../engine/roster/migration';
import type { Character } from '../engine/character/types';
import type { CombatantHalves } from '../engine/roster/sheetEdit';
import { applyLegacyTabs, toLegacyTabs } from '../engine/roster/legacyAdapter';
import { migrateSessionV1toV2 } from '../engine/roster/migration';
import { noteRosterWrite } from '../diagnostics/writeTrace';
import { isObject } from '../engine/bodyShape';

type RosterUpdater = Combatant[] | ((prev: Combatant[]) => Combatant[]);
type TabsUpdater = Tab[] | ((prev: Tab[]) => Tab[]);

export interface UseRosterReturn {
  roster: Combatant[];
  setRoster: (updater: RosterUpdater) => void;
  /** Who this browser is acting as. Local - never synced. */
  actorId: string | null;
  setActorId: (id: string | null) => void;
  /** Fill the target pointer when it points at nobody - see the note on the implementation. */
  pointAtIfUnset: (id: string) => void;
  /** Who this browser is targeting. Local - never synced. */
  targetId: string | null;
  setTargetId: (id: string | null) => void;
  actor: Combatant | null;
  target: Combatant | null;
  /** Write the state half - wounds, conditions, crits, ammo. Undoable, high-frequency. */
  updateCombatantState: (id: string, updater: (state: CombatState) => CombatState) => void;
  /** Write the sheet half - characteristics, talents, weapons. NOT undoable. */
  updateCombatantSheet: (id: string, updater: (sheet: Character) => Character) => void;
  /**
   * One edit that may replace all three halves at once - a sheet edit made
   * on someone already in the fight (engine/roster/sheetEdit). One write,
   * so nobody ever sees the sheet moved and its pins not yet rebased.
   */
  editCombatant: (id: string, change: (c: Combatant) => CombatantHalves) => void;
  appendNarrative: (id: string, entry: unknown) => void;
  /** Undo: put back combat state (and optionally truncate logs). Never touches sheets. */
  restoreStates: (states: Record<string, CombatState>, narrativeCounts?: Record<string, number>) => void;

  // ---- LEGACY SHIM (phase B only) ----
  tabs: Tab[];
  setTabs: (updater: TabsUpdater) => void;
  activeTab: string | null;
  setActiveTab: (id: string | null) => void;
}

interface Params {
  /** A legacy v1 session shape (tabs + attack profiles). Demo scenarios and
   *  fresh sessions both arrive this way, so both seed through the same
   *  migration the Firestore upgrade path uses. */
  initialSession: LegacySession;
}

type SetRosterState = Dispatch<SetStateAction<Combatant[]>>;

export default function useRoster({ initialSession }: Params): UseRosterReturn {
  // Seeding through migrateSessionV1toV2 is what lets a demo scenario keep
  // its bound character and armoury: its atkProfiles/_charBindings become a
  // combatant, exactly as a real legacy session's would.
  const [seed] = useState(() => migrateSessionV1toV2(initialSession));
  const [roster, setRosterState] = useState<Combatant[]>(() => seed.combatants);
  const { targetId, setTargetId, pointAtIfUnset, target } = useTargetPointer(seed, roster);
  const { actorId, setActorId, actorIdRef, actor } = useActorPointer(seed, roster);
  // Every writer reaches the roster through the setter alone, so they are
  // built once.
  const writers = useMemo(() => rosterWriters(setRosterState), []);
  const { tabs, setTabs } = useLegacyTabs(roster, setRosterState, actorIdRef);
  return {
    roster,
    ...writers,
    actorId,
    setActorId,
    pointAtIfUnset,
    targetId,
    setTargetId,
    actor,
    target,
    tabs,
    setTabs,
    activeTab: targetId,
    setActiveTab: setTargetId,
  };
}

/** Whether this id names somebody on the roster. */
const isOnRoster = (roster: Combatant[], id: string | null): id is string => !!id && roster.some((c) => c.id === id);

/**
 * Who this browser is targeting - local, never synced - and the two rules
 * that fill it.
 *
 * `pointAtIfUnset` points the target at somebody, if it is pointing at
 * nobody. With an empty roster a real state, something has to fill the
 * pointer when the first person arrives - and with one combatant there is
 * nothing to choose between. A FUNCTION THE ADD PATHS CALL, never an
 * effect on the roster: an effect cannot tell "I added somebody" from
 * "somebody else's roster arrived", and the version that was one made a
 * JOINING PLAYER act as the GM's first combatant - a character they never
 * chose. That cost a revert and most of a night (v8.29.0).
 *
 * SOMEBODY IS ALWAYS TARGETED, once anybody exists - and that one IS an
 * effect, so it covers a roster that ARRIVED as well as one you added: a
 * player joining a session has an empty roster until the GM's turns up,
 * and with no target their panels have nothing to draw - including the
 * defence prompt, which is why a GM's attack reached nobody (v8.29.0).
 *
 * THE TARGET ONLY, both times. Who you are ACTING AS stays a deliberate
 * choice: that is the contract the app is written against, pointing both
 * made the first arrival attack itself, and setting the actor in the
 * effect made a joining player act as the GM's combatant. Who you ARE
 * stays a choice; who you are looking at does not have to be.
 */
function useTargetPointer(seed: MigrationResult, roster: Combatant[]) {
  const [targetId, setTargetId] = useState<string | null>(seed.targetId);
  const pointAtIfUnset = useCallback((id: string) => {
    if (id) setTargetId((cur) => cur ?? id);
  }, []);
  useEffect(() => {
    if (!roster.length) return;
    setTargetId((cur) => (isOnRoster(roster, cur) ? cur : roster[0].id));
  }, [roster]);
  const target = useMemo(() => roster.find((c) => c.id === targetId) ?? null, [roster, targetId]);
  return { targetId, setTargetId, pointAtIfUnset, target };
}

/** The combatant with this id, when it is one that can act: on the roster and not an Object. */
function actableOn(roster: Combatant[], id: string | null): Combatant | null {
  const c = id ? roster.find((x) => x.id === id) : undefined;
  return c && !isObject(c.sheet) ? c : null;
}

/** Who this browser is acting as - local, never synced. */
function useActorPointer(seed: MigrationResult, roster: Combatant[]) {
  const [actorId, setActorIdState] = useState<string | null>(seed.actorId);
  // Mirrored in a ref so setTabs can see an actor selected earlier in the
  // same React batch (import sets the actor, then replaces the tabs).
  const actorIdRef = useRef<string | null>(seed.actorId);
  const setActorId = useCallback((id: string | null) => {
    actorIdRef.current = id;
    setActorIdState(id);
  }, []);
  const actor = useMemo(() => actableOn(roster, actorId), [roster, actorId]);

  /**
   * An actor who has left the roster stops being the actor - and so does one that is (or was re-bodied as) an Object,
   * which is never the actor (2026-10-26): every door that sets an actor ends here, so none needs its own check.
   *
   * `actor` already resolves to null, so nothing breaks - but the ID
   * lingers, and `pointAtIfUnset` only fires when there is nobody, so
   * the attacker panel would stay empty for ever afterwards and never
   * adopt the next arrival.
   */
  useEffect(() => {
    if (actorId && !actableOn(roster, actorId)) setActorId(null);
  }, [actorId, roster, setActorId]);
  return { actorId, setActorId, actorIdRef, actor };
}

/** One combatant with undo applied: its saved state and its log cut back, when either was saved. */
function restoredCombatant(c: Combatant, state: CombatState | undefined, count: number | undefined): Combatant {
  if (!state && count === undefined) return c;
  return {
    ...c,
    // Sheet and loadout are deliberately untouched: undo reverts combat,
    // not deliberate edits.
    state: state ?? c.state,
    narrativeLog: count === undefined ? c.narrativeLog : c.narrativeLog.slice(0, count),
  };
}

/** The roster after an edit: the SAME array when the edit changed nobody, so React re-renders nothing. */
function edited(prev: Combatant[], next: Combatant[]): Combatant[] {
  return next.every((c, i) => c === prev[i]) ? prev : stampSheetRevs(prev, next);
}

/** `c` with the edited halves; the state clock moves only when the state's content did. */
function withHalves(c: Combatant, halves: CombatantHalves): Combatant {
  // Nothing changed: the SAME combatant, so the roster and every cache keyed on it stay as they are.
  if (halves.sheet === c.sheet && halves.overrides === c.overrides && halves.state === c.state) return c;
  const state = halves.state === c.state ? c.state : bumpRev(c.state, halves.state);
  return { ...c, sheet: halves.sheet, overrides: halves.overrides, state, updatedAt: Date.now() };
}

/**
 * The roster's writers - the two halves kept deliberately apart.
 *
 * EVERY roster transition that can touch a sheet passes through the
 * sheet-clock stamp (engine/roster/rev.ts): the cold half has a dozen
 * mutators across the app, and central stamping is what makes a missed
 * bump impossible. Identity-based, so a wound tick costs nothing there.
 */
function rosterWriters(set: SetRosterState) {
  const update = (id: string, change: (c: Combatant) => Combatant) => (prev: Combatant[]) =>
    prev.map((c) => (c.id === id ? change(c) : c));
  return {
    // Each writer notes itself for the sandbox's write trace (a no-op
    // anywhere else) - who changed the roster, from where (v8.393.5).
    setRoster: (updater: RosterUpdater) => {
      noteRosterWrite('*', 'roster');
      set((prev) => stampSheetRevs(prev, typeof updater === 'function' ? updater(prev) : updater));
    },
    updateCombatantState: (id: string, updater: (state: CombatState) => CombatState) => {
      noteRosterWrite(id, 'state');
      set(update(id, (c) => ({ ...c, state: bumpRev(c.state, updater(c.state)), updatedAt: Date.now() })));
    },
    updateCombatantSheet: (id: string, updater: (sheet: Character) => Character) => {
      noteRosterWrite(id, 'sheet');
      set((prev) =>
        stampSheetRevs(prev, update(id, (c) => ({ ...c, sheet: updater(c.sheet), updatedAt: Date.now() }))(prev)),
      );
    },
    editCombatant: (id: string, change: (c: Combatant) => CombatantHalves) => {
      noteRosterWrite(id, 'sheet');
      set((prev) => edited(prev, update(id, (c) => withHalves(c, change(c)))(prev)));
    },
    appendNarrative: (id: string, entry: unknown) =>
      set(update(id, (c) => ({ ...c, narrativeLog: [...c.narrativeLog, entry as never] }))),
    restoreStates: (states: Record<string, CombatState>, narrativeCounts?: Record<string, number>) =>
      set((prev) => prev.map((c) => restoredCombatant(c, states[c.id], narrativeCounts?.[c.id]))),
  };
}

/**
 * The roster after a legacy tabs update.
 *
 * A tabs array describes TARGETS. Whoever you are acting as is a separate
 * concern, so a WHOLESALE replacement must not delete them from the
 * roster. ONLY WHOLESALE: this used to run for every update, so closing
 * the tab of whoever you were acting as put them straight back - the tab
 * would not close, while the turn-tracker removal beside it had already
 * happened. It looked like the close was ignored, and it was worst for the
 * combatant you were most likely to close, because pointing at somebody
 * makes them the actor (v8.88.0).
 *
 * The legacy tab path edits cold fields too (TargetPanel's updateTab) - it
 * must advance the sheet clock like every other.
 */
function rosterFromTabs(prev: Combatant[], next: Tab[], wholesale: boolean, actorId: string | null): Combatant[] {
  const reconciled = applyLegacyTabs(prev, next);
  const actor = wholesale ? prev.find((c) => c.id === actorId) : undefined;
  if (actor && !reconciled.some((c) => c.id === actor.id)) {
    return stampSheetRevs(prev, [...reconciled, { ...actor, order: reconciled.length }]);
  }
  return stampSheetRevs(prev, reconciled);
}

/**
 * LEGACY SHIM. `tabs` is derived, not stored. projectDefender is
 * identity-cached, so combatants that did not change hand back the very
 * same Target object and memoized children below can still skip
 * re-rendering.
 */
function useLegacyTabs(roster: Combatant[], set: SetRosterState, actorIdRef: MutableRefObject<string | null>) {
  const tabs = useMemo(() => toLegacyTabs(roster), [roster]);
  const setTabs = useCallback(
    (updater: TabsUpdater) => {
      // WHOLESALE, or a deliberate edit? An ARRAY replaces the roster
      // outright - that is session import and the Firestore full-snapshot.
      // A FUNCTION is somebody editing what is there: adding a tab,
      // duplicating one, closing one.
      const wholesale = typeof updater !== 'function';
      set((prev) =>
        rosterFromTabs(prev, wholesale ? updater : updater(toLegacyTabs(prev)), wholesale, actorIdRef.current),
      );
    },
    [set, actorIdRef],
  );
  return { tabs, setTabs };
}
